import type { Translations } from './ru'

export const enTranslations: Translations = {
  nav: {
    tools: 'Tools',
    qrGenerator: 'QR code',
    barcodeGenerator: 'Barcode',
    barcodeScanner: 'Barcode scanner',
    qrScanner: 'Scanner',
    blog: 'Articles',
  },
  langSwitcher: {
    aria: 'Interface language',
  },
  seo: {
    index: {
      title: 'Online QR and barcode tools - qrka.ru',
      description:
        'Free qrka.ru tools: create QR codes, generate EAN/UPC/Code 128 barcodes and read QR codes or barcodes from images and camera.',
    },
    qrGenerator: {
      title: 'QR code generator online - free PNG and SVG export - qrka.ru',
      description:
        'Create a static QR code for a URL, text, Wi-Fi, vCard, email, SMS or phone number. Customize it and export PNG or SVG locally in your browser.',
    },
    qrScanner: {
      title: 'Online QR code scanner for screenshots, photos and camera - qrka.ru',
      description:
        'Scan a QR code online: paste a screenshot with Ctrl+V, choose a photo or start the camera. Everything runs in your browser, and links open only after your click.',
    },
    barcodeGenerator: {
      title: 'Barcode generator online - EAN, UPC, Code 128 - qrka.ru',
      description:
        'Create EAN-13, EAN-8, UPC-A, Code 128 or Code 39 barcodes online. Validate the format and download PNG or SVG for free.',
    },
    barcodeScanner: {
      title: 'Online barcode scanner from an image or camera - qrka.ru',
      description:
        'Recognize EAN-13, EAN-8, UPC-A or Code 128 from an image or camera locally in your browser.',
    },
    blog: {
      title: 'QR code articles, printing and generation - qrka.ru',
      description:
        'Practical articles about static QR codes, PNG and SVG generation, contrast, printing, Wi-Fi QR and choosing between static and dynamic QR.',
    },
  },
  common: {
    qrTools: 'QR tools',
    allBlogPosts: 'All articles',
    updated: 'updated',
    faq: 'FAQ',
    processing: {
      local: {
        label: 'Local',
        title: 'Data is processed directly in your browser and is not sent to a server',
      },
    },
  },
  home: {
    hero: {
      title: 'QR codes and barcodes online',
      description:
        'Create a QR code right here: free, no sign-up, your data stays in the browser.',
      scanAction: 'Scan a QR code',
    },
    tools: {
      title: 'All tools',
      generator: {
        title: 'QR code generator',
        description:
          'Create a static QR code for a URL, text, Wi-Fi, vCard, email, SMS or phone number.',
        cta: 'Open generator',
      },
      scanner: {
        title: 'QR code scanner',
        description:
          'Read a QR code from a photo, screenshot or camera without installing an app.',
        cta: 'Open scanner',
      },
      barcodeScanner: {
        title: 'Barcode scanner',
        description:
          'Recognize EAN-13, EAN-8, UPC-A or Code 128 from an image or camera.',
        cta: 'Scan barcode',
      },
      barcode: {
        title: 'Barcode generator',
        description:
          'Create EAN-13, EAN-8, UPC-A, Code 128 or Code 39 for labels, documents or internal inventory.',
        cta: 'Open generator',
      },
    },
  },
  blog: {
    breadcrumb: 'Articles',
    title: 'QR code articles',
    description:
      'Practical notes on static and dynamic QR codes, contrast, print, SVG, Wi-Fi and no-account generation.',
    empty: 'No articles published yet.',
    languageAria: 'Article language',
    postLanguageAria: 'Article language',
    localeName: {
      ru: 'Русский',
      en: 'English',
    },
    notFoundTitle: 'Article not found',
    notFoundDescription: 'The article may have moved, or the URL may be incorrect.',
    relatedTools: 'Related tools',
    relatedToolDescription: {
      qrGenerator:
        'Create a static QR code for a URL, text, Wi-Fi, vCard, email, SMS or phone number.',
    },
  },
  schema: {
    name: 'Online QR code generator',
    description:
      'Create static QR codes for URLs, text, Wi-Fi, vCard, email, SMS and phone numbers locally in your browser.',
    howTo: [
      'Choose the QR code type: URL, text, Wi-Fi, vCard, email, SMS or phone.',
      'Enter the data and adjust colors, size and error correction level.',
      'Check the contrast, generate the QR code and download PNG or SVG.',
    ],
    faq: [
      {
        question: 'Is this a static or dynamic QR code?',
        answer:
          'Static. The data is written directly into the QR code, so the destination cannot be changed after printing and there is no scan analytics.',
      },
      {
        question: 'Are files or data uploaded to a server?',
        answer:
          'No. Generation happens in the browser without uploading text, links, Wi-Fi passwords or contacts to a server.',
      },
      {
        question: 'Can I download SVG for free?',
        answer:
          'Yes. PNG and SVG are available with no sign-up, watermarks or paid limits.',
      },
    ],
  },
  scannerSchema: {
    name: 'QR scanner for photos, screenshots and camera',
    description:
      'Read QR codes from a photo, screenshot or camera locally in your browser without installing an app.',
    howTo: [
      'Paste a screenshot with Ctrl+V, choose a photo with a QR code, or press “Start camera”.',
      'Wait for local QR recognition in the browser.',
      'Review the result, copy the text or open the link with a separate button.',
    ],
    faq: [
      {
        question: 'How do I read a QR code from a screenshot?',
        answer:
          'Take a screenshot, open this page and press Ctrl+V or the “Paste from clipboard” button. On a phone, save the screenshot and choose it as a file.',
      },
      {
        question: 'Are photos or camera video uploaded to a server?',
        answer:
          'No. Recognition runs locally in the browser. Camera frames and uploaded images are not sent to qrka.ru servers.',
      },
      {
        question: 'Why does the browser ask for camera access?',
        answer:
          'Access is needed only for camera scanning. If you deny it, you can still scan a QR code from an image or screenshot.',
      },
      {
        question: 'Does the scanner open links automatically?',
        answer:
          'No. The scanner shows the destination and opens the link only after a separate click.',
      },
    ],
  },
  barcodeScannerSchema: {
    name: 'Barcode scanner from an image or camera',
    description:
      'Recognize EAN-13, EAN-8, UPC-A and Code 128 locally in your browser from an image or camera.',
    howTo: [
      'Upload a PNG, JPG or WebP barcode image, or explicitly start the camera.',
      'Point the camera at the linear barcode and wait for local recognition.',
      'Check the detected format and copy the raw value.',
    ],
    faq: [
      {
        question: 'Which formats are supported?',
        answer: 'The scanner recognizes EAN-13, EAN-8, UPC-A and Code 128. Other formats are not supported yet.',
      },
      {
        question: 'Are photos or camera frames sent to a server?',
        answer: 'No. The recognition module loads from qrka.ru, while recognition runs locally in your browser.',
      },
      {
        question: 'Does the scanner find a product or price?',
        answer: 'No. It only shows the barcode format and raw value without querying a product database.',
      },
    ],
  },
  barcodeSchema: {
    name: 'Online barcode generator',
    description:
      'Create EAN-13, EAN-8, UPC-A, Code 128 and Code 39 barcodes with PNG and SVG export.',
    howTo: [
      'Choose the barcode format: EAN-13, EAN-8, UPC-A, Code 128 or Code 39.',
      'Enter the value, check the format limits and adjust the appearance.',
      'Check the barcode and download the image as PNG or SVG.',
    ],
    faq: [
      {
        question: 'Does this generator register product codes?',
        answer:
          'No. The tool creates a barcode image, but it does not issue or register official GS1 or marketplace product codes.',
      },
      {
        question: 'Which formats are supported?',
        answer:
          'EAN-13, EAN-8, UPC-A, Code 128 and Code 39 are available.',
      },
      {
        question: 'Can I download SVG for free?',
        answer:
          'Yes. PNG and SVG are available for free, with no sign-up, watermarks or waiting timer.',
      },
    ],
  },
  qr: {
    hero: {
      title: 'QR code online with no sign-up',
      description:
        'Create a QR code for a link, text, Wi-Fi, business card, email, SMS or phone number. Everything runs in your browser: data is not sent to a server, and SVG is free.',
    },
    typeSelectorLabel: 'QR code data type',
    howToTitle: 'How to create a QR code',
    data: {
      description:
        'A static QR code cannot be changed after printing, and there are no scan statistics.',
    },
    typeOptions: {
      url: { label: 'Link' },
      text: { label: 'Text' },
      wifi: { label: 'Wi-Fi' },
      vcard: { label: 'vCard' },
      email: { label: 'Email' },
      sms: { label: 'SMS' },
      phone: { label: 'Phone' },
    },
    fields: {
      url: 'Link',
      urlPlaceholder: 'https://example.com',
      text: 'Text',
      textPlaceholder: 'Enter text for the QR code',
      wifiSsid: 'Network name',
      wifiSsidPlaceholder: 'My Wi-Fi',
      wifiPassword: 'Password',
      wifiPasswordPlaceholder: 'Leave empty for an open network',
      wifiEncryption: 'Network type',
      wifiWpa: 'WPA/WPA2',
      wifiWep: 'WEP',
      wifiOpen: 'No password',
      wifiHidden: 'Hidden network',
      vcardName: 'Name',
      vcardOrg: 'Organization',
      vcardPhone: 'Phone',
      vcardEmail: 'Email',
      vcardUrl: 'Website',
      emailAddress: 'Email',
      emailSubject: 'Subject',
      emailBody: 'Email body',
      smsPhone: 'Phone number',
      smsBody: 'Message',
      phone: 'Phone number',
      phonePlaceholder: '+12025550123',
    },
    textDensity: {
      counter: (chars: number, bytes: number) => `${chars} chars, ${bytes} bytes`,
      hint: 'Keep text short for reliable scanning.',
      warning:
        'The QR code will become dense. For print, increase the image size and test it with a phone.',
      danger:
        'This text is too large for a reliable QR code. Shorten it or use a link to a document.',
    },
    appearance: {
      title: 'Appearance',
      description:
        'By default the QR code has high contrast and a clear margin around it.',
      foreground: 'QR color',
      background: 'Background',
      size: 'Size',
      correction: 'Error correction',
      contrast: 'Contrast',
      contrastOk: 'Good for a regular phone camera.',
      contrastBad: 'Contrast is too low: the QR code may not scan reliably.',
      show: 'Customize',
      hide: 'Hide',
    },
    preview: {
      title: 'Your QR code',
      description: 'Check the QR code with a phone camera before printing or sharing it.',
      empty: 'The QR code appears after you enter the data.',
      generating: 'Updating the QR code...',
      imageAlt: 'Generated QR code',
      ready: 'QR code ready.',
      stale: 'Data changed, updating the QR code…',
      invalid: 'QR code not generated: check the data.',
      downloadPng: 'Download PNG',
      downloadSvg: 'Download SVG',
      retry: 'Retry',
    },
    faqTitle: 'FAQ',
    errors: {
      lowContrast: 'Increase color contrast to at least 4.5:1.',
      previewUnavailable: 'The QR code is not available yet.',
      urlRequired: 'Enter a link.',
      invalidUrl: 'Enter a valid link with a domain.',
      textRequired: 'Enter text for the QR code.',
      wifiSsidRequired: 'Enter the Wi-Fi network name.',
      emptyVcard: 'Add a name, phone number or email for the contact.',
      emailRequired: 'Enter an email address.',
      smsPhoneRequired: 'Enter a phone number for SMS.',
      phoneRequired: 'Enter a phone number.',
      emptyPayload: 'Fill in QR code data.',
      generationFailed:
        'Could not generate the QR code. Reduce the text size or check the entered data.',
    },
  },
  barcode: {
    hero: {
      title: 'Online barcode generator',
      description:
        'Choose EAN-13, EAN-8, UPC-A, Code 128 or Code 39, enter a value and download the barcode. Everything runs in your browser, with no sign-up or watermarks.',
    },
    formatOptions: {
      ean13: { label: 'EAN-13' },
      ean8: { label: 'EAN-8' },
      upc: { label: 'UPC-A' },
      code128: { label: 'Code 128' },
      code39: { label: 'Code 39' },
    },
    formatHelp: {
      ean13: 'Enter 12 digits to add the check digit automatically, or 13 digits with a valid checksum.',
      ean8: 'Enter 7 digits for an automatic check digit, or a full 8-digit code.',
      upc: 'Enter 11 digits for an automatic check digit, or a full 12-digit UPC-A.',
      code128: 'Good for internal codes, inventory IDs and arbitrary text up to 80 characters.',
      code39: 'Supports uppercase Latin letters, digits, spaces and the symbols - . $ / + %.',
    },
    fields: {
      value: 'Value',
      valuePlaceholder: {
        ean13: '460123456789',
        ean8: '1234567',
        upc: '03600029145',
        code128: 'ORDER-2026-001',
        code39: 'ITEM-123',
      },
    },
    appearance: {
      title: 'Appearance',
      description:
        'Keep high contrast and enough quiet area around the code for reliable scanning.',
      lineColor: 'Line color',
      background: 'Background',
      width: 'Line width',
      height: 'Height',
      displayValue: 'Show value under the barcode',
    },
    preview: {
      title: 'Your barcode',
      description: 'Check the code with a scanner before printing a batch of labels.',
      empty: 'Enter a value to see the barcode.',
      pendingUpdate: 'Updating...',
      generate: 'Generate',
      generating: 'Generating...',
      ready: 'Barcode ready.',
      invalid: 'Barcode not generated: check the data.',
      downloadPng: 'Download PNG',
      downloadSvg: 'Download SVG',
      normalized: 'Final value',
      registrationNotice:
        'Important: this tool creates a code image. It does not register a product code with GS1, marketplaces or warehouse systems.',
    },
    faqTitle: 'FAQ',
    faq: [
      {
        question: 'Can I use the code for a retail product?',
        answer:
          'You can print the image, but the number itself must be issued or allowed by your inventory system, GS1 or the marketplace where the product is sold.',
      },
      {
        question: 'Why does EAN/UPC reject some digit strings?',
        answer:
          'These formats have fixed lengths and a check digit. If you enter a full code, the generator validates the checksum.',
      },
      {
        question: 'How is Code 128 different from Code 39?',
        answer:
          'Code 128 is more compact and supports more characters. Code 39 is simpler, but usually longer and limited to uppercase Latin letters, digits and a few symbols.',
      },
    ],
    errors: {
      emptyValue: 'Enter a barcode value.',
      previewUnavailable: 'The barcode is not available yet.',
      ean13: 'EAN-13 must contain 12 digits, or 13 digits with a valid checksum.',
      ean8: 'EAN-8 must contain 7 digits, or 8 digits with a valid checksum.',
      upc: 'UPC-A must contain 11 digits, or 12 digits with a valid checksum.',
      code128: 'Code 128 must be non-empty and no longer than 80 characters.',
      code39: 'Code 39 supports only A-Z, digits, spaces and the symbols - . $ / + %, up to 80 characters.',
      generationFailed: 'Could not generate the barcode. Check the format and value.',
      pngExportFailed: 'Could not prepare the PNG. Try downloading SVG instead.',
      svgExportFailed: 'Could not prepare the SVG. Try again.',
    },
  },
  scanner: {
    hero: {
      title: 'Online QR scanner: screenshot, photo, camera',
      description:
        'Paste a screenshot, choose a photo or start the camera. Recognition runs in your browser, and links never open on their own.',
    },
    upload: {
      dropDescription: 'PNG, JPG or WebP, up to 20 MB. The file is processed locally.',
      choose: 'Choose a photo or screenshot',
      paste: 'Paste from clipboard',
      pasteHint: 'Or press Ctrl+V on this page.',
      reading: 'Reading...',
    },
    camera: {
      placeholder: 'Camera preview appears here after you allow access.',
      permissionHint: 'Permission is requested only after you press the button. Image upload remains available if you deny it.',
      previewTitle: 'Camera is on',
      start: 'Start camera',
      starting: 'Requesting access...',
      stop: 'Stop',
    },
    result: {
      title: 'Result',
      description: 'Review the content before copying it or opening a link.',
      empty: 'Paste a screenshot, choose a photo or start the camera to read a QR code.',
      sourceUpload: 'Source: image',
      sourceCamera: 'Source: camera',
      destination: 'Destination',
      openLink: 'Open link',
      copy: 'Copy',
      copied: 'Copied',
    },
    howToTitle: 'How to scan a QR code',
    faqTitle: 'FAQ',
    errors: {
      no_qr_found: 'No QR code found. Try a sharper image without glare.',
      multiple_codes: 'Multiple QR codes were found. Crop the image so it contains only one code.',
      unsupported_file: 'Could not read this file. Upload a valid PNG, JPG or WebP image.',
      file_too_large: 'This image is too large. Limits: 20 MB, 12,000 px per side and 40 MP.',
      scanner_failed: 'Could not start recognition. Refresh the page and try again.',
      permission_denied: 'Camera access was denied. Allow camera access in the browser or upload an image.',
      unsupported_browser: 'This browser does not support camera scanning. Upload an image with a QR code instead.',
      camera_failed: 'Could not start the camera. Check access and try again.',
      camera_timeout: 'No QR code was found in 30 seconds. Try again or upload a photo or screenshot.',
      clipboard_empty: 'There is no image in the clipboard. Copy a screenshot and try again.',
      clipboard_unavailable: 'The browser did not allow reading the clipboard. Press Ctrl+V on the page or choose a file.',
    },
  },
  barcodeScanner: {
    hero: {
      title: 'Online barcode scanner from an image or camera',
      description:
        'Recognize EAN-13, EAN-8, UPC-A or Code 128 locally in your browser. The camera starts only after your click.',
    },
    upload: {
      dropDescription: 'PNG, JPG or WebP, up to 20 MB. The file is not sent to a server.',
      choose: 'Choose a photo',
      paste: 'Paste from clipboard',
      pasteHint: 'Or press Ctrl+V on this page.',
      reading: 'Recognizing...',
    },
    camera: {
      placeholder: 'Point the camera at the barcode and keep the bars in focus.',
      permissionHint: 'Permission is requested only after this action. Upload remains available if denied.',
      previewTitle: 'Camera is on',
      start: 'Start camera',
      starting: 'Requesting access...',
      stop: 'Stop',
    },
    result: {
      title: 'Result',
      description: 'The scanner shows the detected format and raw value.',
      empty: 'Upload an image or start the camera to recognize a barcode.',
      sourceUpload: 'Source: image',
      sourceCamera: 'Source: camera',
      destination: 'Destination',
      openLink: 'Open link',
      copy: 'Copy value',
      copied: 'Copied',
    },
    howToTitle: 'How to scan a barcode',
    faqTitle: 'FAQ',
    relatedQrScanner: 'Open the QR code scanner',
    errors: {
      no_qr_found: 'No supported barcode was found. Try a sharper image without glare.',
      multiple_codes: 'Multiple barcodes were found. Check each result.',
      unsupported_file: 'Could not read this file. Upload a valid PNG, JPG or WebP image.',
      file_too_large: 'This image is too large. Limits: 20 MB, 12,000 px per side and 40 MP.',
      scanner_failed: 'Could not start recognition. Refresh the page and try again.',
      permission_denied: 'Camera access was denied. Allow camera access or upload an image.',
      unsupported_browser: 'This browser does not support the camera. Upload a barcode image instead.',
      camera_failed: 'Could not start the camera. Check access and try again.',
      camera_timeout: 'No barcode was found in 30 seconds. Try again or upload a photo.',
      clipboard_empty: 'There is no image in the clipboard. Copy an image and try again.',
      clipboard_unavailable: 'The browser did not allow reading the clipboard. Press Ctrl+V on the page or choose a file.',
    },
  },
}
